import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { getAccessToken } from "../../awsAuth";
import Chat from "./Chat";
import "./Messages.scss";
import DynamicHeader from "../Header/DynamicHeader";
import deleteIcon from "./img/delete-chat.png";

const DEFAULT_AVATAR =
  "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

/* ==== local broadcast key for messages ==== */
const MSG_MARK_KEY = "cvanto:messages:mark";
function broadcastMessagesClear(threadId) {
  try {
    localStorage.setItem(
      MSG_MARK_KEY,
      JSON.stringify({ ts: Date.now(), threadId })
    );
  } catch {}
  try {
    window.dispatchEvent(new Event(MSG_MARK_KEY));
  } catch {}
}

/* ==== utils ==== */
function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}
function useIsMobile(breakpoint = 1023) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = (e) => setIsMobile(e.matches);
    try {
      mql.addEventListener("change", onChange);
    } catch {
      mql.addListener(onChange);
    }
    setIsMobile(mql.matches);
    return () => {
      try {
        mql.removeEventListener("change", onChange);
      } catch {
        mql.removeListener(onChange);
      }
    };
  }, [breakpoint]);
  return isMobile;
}

const Messages = () => {
  const navigate = useNavigate();
  const { userId } = useAuth();

  const params = useParams();
  const conversationId = params.threadId || params.conversationId || null;

  const q = useQuery();
  const toUserId = q.get("to") || null;

  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [filter, setFilter] = useState("");
  const [activeThreadId, setActiveThreadId] = useState(conversationId || null);
  const [ephemeralPeerId, setEphemeralPeerId] = useState(toUserId);

  const profileCacheRef = useRef({});

  const isMobile = useIsMobile(1023);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const isStandalone =
      window.matchMedia &&
      window.matchMedia("(display-mode: standalone)").matches;

    if (!isStandalone) return;

    document.body.classList.add("messages-no-overscroll");

    return () => {
      document.body.classList.remove("messages-no-overscroll");
    };
  }, []);


  const chatOpen = Boolean(activeThreadId || ephemeralPeerId);
  const rootClassName = `messages ${
    isMobile && chatOpen ? "messages--chat-open" : ""
  }`;

  const apiFetch = useCallback(
  async (url, options = {}) => {
    const fullUrl =
      url.startsWith("http://") || url.startsWith("https://")
        ? url
        : `${API_BASE}${url}`;

    const token = await getAccessToken();

    return fetch(fullUrl, {
      ...options,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
  },
  []
);

  const openSwipeCloserRef = useRef(null);
  const registerOpenedSwipe = useCallback((closerFn) => {
    if (openSwipeCloserRef.current && openSwipeCloserRef.current !== closerFn) {
      try {
        openSwipeCloserRef.current();
      } catch {}
    }
    openSwipeCloserRef.current = closerFn;
  }, []);
  const clearOpenedSwipeIf = useCallback((closerFn) => {
    if (openSwipeCloserRef.current === closerFn) {
      openSwipeCloserRef.current = null;
    }
  }, []);
  const closeAnyOpenSwipe = useCallback(() => {
    if (openSwipeCloserRef.current) {
      try {
        openSwipeCloserRef.current();
      } catch {}
      openSwipeCloserRef.current = null;
    }
  }, []);

  /* ==== load conversations ==== */
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await apiFetch(`/api/messages/conversations`);
        const raw = res.ok ? await res.json() : [];
        const data = Array.isArray(raw) ? raw : [];
        const convs = data.map((it) => ({
          threadId: it.threadId,
          peerId: it.peerId,
          lastText: it.lastText || "",
          lastAt: Number(it.lastAt || 0),
          unread: Number(it.unread || 0),
          peerUsername: it.peerUsername || null,
          peerAvatar: it.peerAvatar || null,
        }));
        if (!cancelled) setConversations(convs);
      } catch (e) {
        console.error("load conversations error", e);
        if (!cancelled) setConversations([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, apiFetch]);

  /* ==== peer profile cache ==== */
  const fetchPeerProfile = useCallback(async (id) => {
    if (!id) return null;
    if (profileCacheRef.current[id]) return profileCacheRef.current[id];
    try {
      const r = await fetch(
        `${API_BASE}/api/account/${encodeURIComponent(id)}`
      );
      const fallback = { username: "Користувач", avatar: DEFAULT_AVATAR };
      if (!r.ok) {
        profileCacheRef.current[id] = fallback;
        return fallback;
      }
      const u = await r.json();
      const prof = {
        username: u.username || "Користувач",
        avatar: u.avatar || DEFAULT_AVATAR,
      };
      profileCacheRef.current[id] = prof;
      return prof;
    } catch {
      const fallback = { username: "Користувач", avatar: DEFAULT_AVATAR };
      profileCacheRef.current[id] = fallback;
      return fallback;
    }
  }, []);

  useEffect(() => {
    const idsToFetch = Array.from(
      new Set(
        conversations
          .filter((c) => c.peerId && (!c.peerUsername || !c.peerAvatar))
          .map((c) => c.peerId)
      )
    ).filter((id) => !profileCacheRef.current[id]);

    if (idsToFetch.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        await Promise.all(idsToFetch.map((id) => fetchPeerProfile(id)));
        if (cancelled) return;
        setConversations((prev) =>
          prev.map((c) => {
            if (!c.peerId) return c;
            const prof = profileCacheRef.current[c.peerId];
            if (!prof) return c;
            return {
              ...c,
              peerUsername: c.peerUsername || prof.username,
              peerAvatar: c.peerAvatar || prof.avatar,
            };
          })
        );
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [conversations, fetchPeerProfile]);

  useEffect(() => {
    setActiveThreadId(conversationId || null);
  }, [conversationId]);
  useEffect(() => {
    setEphemeralPeerId(toUserId);
  }, [toUserId]);

  const filtered = useMemo(() => {
    if (!filter.trim()) return conversations;
    const f = filter.trim().toLowerCase();
    return conversations.filter((c) => {
      const u = (c.peerUsername || c.peerId || "").toLowerCase();
      return u.includes(f);
    });
  }, [conversations, filter]);

  /* ==== mark thread read helpers ==== */
  const markThrottleRef = useRef({}); // threadId -> lastTs

  const markThreadRead = useCallback(
    async (threadId) => {
      if (!threadId || !userId) return;

      const now = Date.now();
      const last = markThrottleRef.current[threadId] || 0;
      if (now - last < 1200) return;
      markThrottleRef.current[threadId] = now;

      setConversations((prev) =>
        prev.map((c) =>
          c.threadId === threadId ? { ...c, unread: 0 } : c
        )
      );

      broadcastMessagesClear(threadId);

      try {
        await apiFetch(`/api/messages/chat/read`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ threadId }),
        });
      } catch {}
    },
    [userId, apiFetch]
  );

  /* ==== open conversation actions ==== */
  const handleOpenConversation = (threadId) => {
    setActiveThreadId(threadId);
    setEphemeralPeerId(null);
    navigate(`/messages/${threadId}`, { replace: true });
    markThreadRead(threadId);
  };

  useEffect(() => {
    if (activeThreadId) {
      markThreadRead(activeThreadId);
    }
  }, [activeThreadId, markThreadRead]);

    const handleConversationCreated = (threadId) => {
    const peerId = ephemeralPeerId || toUserId || null;

    setActiveThreadId(threadId);
    setEphemeralPeerId(null);
    navigate(`/messages/${threadId}`, { replace: true });

    setConversations((prev) => {
      if (prev.some((c) => c.threadId === threadId)) return prev;

      const cached = peerId ? profileCacheRef.current[peerId] : null;

      return [
        {
          threadId,
          peerId,
          lastText: "…",
          lastAt: Date.now(),
          unread: 0,
          peerUsername: cached?.username || null,
          peerAvatar: cached?.avatar || null,
        },
        ...prev,
      ];
    });

    if (peerId && !profileCacheRef.current[peerId]) {
      fetchPeerProfile(peerId).then((prof) => {
        if (!prof) return;
        setConversations((prev) =>
          prev.map((c) =>
            c.threadId === threadId
              ? {
                  ...c,
                  peerUsername: c.peerUsername || prof.username,
                  peerAvatar: c.peerAvatar || prof.avatar,
                }
              : c
          )
        );
      }).catch(() => {});
    }

    markThreadRead(threadId);
  };


  const onBackFromChat = () => {
    setActiveThreadId(null);
    setEphemeralPeerId(null);
    if (isMobile) navigate("/messages", { replace: true });
  };

  const deleteConversation = useCallback(
    async (threadId) => {
      try {
        const ok = window.confirm(
          "Видалити діалог лише для вас? Історія лишиться у співрозмовника."
        );
        if (!ok) return false;
        const res = await apiFetch(`/api/messages/conversations/delete`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ threadId }),
        });
        if (!res.ok) {
          const msg =
            (await res.json().catch(() => ({})))?.error ||
            "Не вдалося видалити";
          alert(msg);
          return false;
        }
        setConversations((prev) =>
          prev.filter((c) => c.threadId !== threadId)
        );
        if (activeThreadId === threadId) onBackFromChat();
        return true;
      } catch (e) {
        console.error("delete conv error", e);
        alert("Помилка видалення");
        return false;
      }
    },
    [apiFetch, activeThreadId, isMobile]
  );

  const handleListPointerDown = (e) => {
    const row = e.target.closest?.(".thread-swipe");
    if (!row || !row.classList.contains("is-open")) {
      closeAnyOpenSwipe();
    }
  };

  return (
    <>
      {(!isMobile || (isMobile && !chatOpen)) && (
        <DynamicHeader
          title="Повідомлення"
          variant="glass"
          size="compact"
          hideOnScroll={true}
          showBack={true}
          withSpacer={true}
        />
      )}

      <div className={rootClassName}>
        <div className="messages__wrap">
          <aside className="messages__sidebar">
            <div className="messages__topbar">
              <button
                className="topbar__back hide-on-desktop"
                onClick={() => navigate(-1)}
                aria-label="Назад"
              >
                ←
              </button>
              <div className="topbar__title">Чати</div>
            </div>

            <div className="messages__search">
              <div className="search__field">
                <svg className="search__icon" viewBox="0 0 24 24">
                  <circle
                    cx="11"
                    cy="11"
                    r="7"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                  />
                  <line
                    x1="21"
                    y1="21"
                    x2="16.65"
                    y2="16.65"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Пошук по діалогах…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
                {filter && (
                  <button
                    className="search__clear"
                    onClick={() => setFilter("")}
                    aria-label="Очистити"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            <div
              className="thread-list"
              onMouseDown={handleListPointerDown}
              onTouchStart={handleListPointerDown}
            >
              {loading && (
                <>
                  <div className="thread-skeleton">
                    <div className="sk-avatar" />
                    <div className="sk-line sk-name" />
                    <div className="sk-line sk-preview" />
                  </div>
                  <div className="thread-skeleton">
                    <div className="sk-avatar" />
                    <div className="sk-line sk-name" />
                    <div className="sk-line sk-preview" />
                  </div>
                </>
              )}

              {!loading && filtered.length === 0 && (
                <div className="thread-empty">
                  У вас поки що немає діалогів з іншими користувачами
                </div>
              )}

              {!loading &&
                filtered.map((c) => {
                  const active = activeThreadId === c.threadId;
                  return (
                    <ThreadRow
                      key={c.threadId}
                      conv={c}
                      active={!!active}
                      onOpen={() => handleOpenConversation(c.threadId)}
                      onDelete={() => deleteConversation(c.threadId)}
                      onRegisterOpenedSwipe={registerOpenedSwipe}
                      onUnregisterSwipe={clearOpenedSwipeIf}
                    />
                  );
                })}
            </div>
          </aside>

          <main
            className="messages__content"
            onMouseDown={closeAnyOpenSwipe}
            onTouchStart={closeAnyOpenSwipe}
          >
            {ephemeralPeerId && !activeThreadId && (
              <Chat
                key={`ephemeral-${ephemeralPeerId}`}
                threadId={null}
                peerId={ephemeralPeerId}
                onBack={onBackFromChat}
                onConversationCreated={handleConversationCreated}
              />
            )}

            {activeThreadId && (
              <Chat
                key={activeThreadId}
                threadId={activeThreadId}
                peerId={null}
                onBack={onBackFromChat}
                onConversationCreated={handleConversationCreated}
              />
            )}

            {!ephemeralPeerId && !activeThreadId && (
              <div className="chat-placeholder">
                <div className="placeholder-illustration">💬</div>
                <h3>Виберіть діалог</h3>
                <p>
                  Або відкрийте профіль користувача і натисніть «Повідомлення»
                </p>
              </div>
            )}
          </main>
        </div>
      </div>
    </>
  );
};

export default Messages;

const SWIPE_MAX = 88;

function ThreadRow({
  conv,
  active,
  onOpen,
  onDelete,
  onRegisterOpenedSwipe,
  onUnregisterSwipe,
}) {
  const rootRef = useRef(null);
  const wrapRef = useRef(null);
  const touchRef = useRef({
    x: 0,
    y: 0,
    dx: 0,
    dy: 0,
    swiping: false,
    opened: false,
  });

  const openActions = (open) => {
    touchRef.current.opened = open;
    if (!rootRef.current || !wrapRef.current) return;
    rootRef.current.classList.toggle("is-open", open);
    if (!open) {
      wrapRef.current.style.transform = "";
      wrapRef.current.style.transition = "";
      rootRef.current.classList.remove("is-dragging");
      onUnregisterSwipe?.(closeSelf);
    } else {
      onRegisterOpenedSwipe?.(closeSelf);
    }
  };

  const closeSelf = () => openActions(false);

  const onContextMenu = (e) => {
    e.preventDefault();
    onDelete?.();
  };

  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchRef.current.x = t.clientX;
    touchRef.current.y = t.clientY;
    touchRef.current.dx = 0;
    touchRef.current.dy = 0;
    touchRef.current.swiping = false;
    if (wrapRef.current) wrapRef.current.classList.remove("is-dragging");
  };

  const onTouchMove = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    touchRef.current.dx = dx;
    touchRef.current.dy = dy;

    if (touchRef.current.opened && dx > 6 && Math.abs(dx) > Math.abs(dy)) {
      openActions(false);
      return;
    }

    if (
      !touchRef.current.swiping &&
      Math.abs(dx) > 4 &&
      Math.abs(dx) > Math.abs(dy)
    ) {
      touchRef.current.swiping = true;
      rootRef.current && rootRef.current.classList.add("is-dragging");
      openActions(true);
      return;
    }
  };

  const onTouchEnd = () => {
    if (!wrapRef.current) return;
    rootRef.current && rootRef.current.classList.remove("is-dragging");
  };

  const handleClickRow = () => {
    if (touchRef.current.opened) {
      openActions(false);
      return;
    }
    onOpen?.();
  };

  const handleDeleteTap = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete?.();
  };

  return (
    <div
      className={`thread-swipe ${active ? "is-active" : ""}`}
      ref={rootRef}
      onContextMenu={onContextMenu}
      style={{ "--swipe-open-x": `-${SWIPE_MAX}px` }}
    >
      <div className="thread-swipe__actions">
        <button
          className="thread-swipe__delete"
          onClick={handleDeleteTap}
          onTouchEnd={handleDeleteTap}
          aria-label="Видалити"
        >
          <img src={deleteIcon} alt="" />
        </button>
      </div>

      <div
        className="thread-item"
        ref={wrapRef}
        onClick={handleClickRow}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        <div className="thread-item__avatar">
          <img
            src={conv.peerAvatar || DEFAULT_AVATAR}
            alt=""
            onError={(e) => (e.currentTarget.src = DEFAULT_AVATAR)}
          />
        </div>

        <div className="thread-item__top">
          <div className="name">
            {conv.peerUsername || conv.peerId || "Користувач"}
          </div>
          <div className="time">
            {conv.lastAt
              ? new Date(conv.lastAt).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : ""}
          </div>
        </div>

        <div className="thread-item__bottom">
          <div className="preview">{conv.lastText || "…"}</div>
          {conv.unread > 0 && (
            <span className={`unread ${active ? "" : "pulse"}`}>
              {conv.unread}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
